import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { toast } from '@app/components/ui/Toast/toast'
import { DOCUMENTS_QUERY_KEY, FOLDERS_QUERY_KEY } from '../documents.constants'
import { Folder } from '../collections.types'
import {
  addDocumentToFolder,
  createFolder,
  CreateFolderPayload,
  deleteFolder,
  fetchFolders,
  isDuplicateFolderNameError,
  removeDocumentFromFolder,
  updateFolder,
  UpdateFolderPayload,
} from '../service/collections.service'

// 403 means the user lost access to the Document itself, not the collection.
function movePermissionDeniedMessage(error: unknown, fallback: string): string {
  const status = isAxiosError(error) ? error.response?.status : undefined
  return status === 403 ? "You don't have access to this document." : fallback
}

export const useFetchFolders = () => {
  return useQuery<Folder[]>({
    queryKey: [FOLDERS_QUERY_KEY],
    queryFn: fetchFolders,
  })
}

export const useCreateFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateFolderPayload) => createFolder(payload),
    onSuccess: () => {
      toast({ description: 'Collection created.', position: 'top-right', type: 'success' })
      queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY] })
    },
    onError: (error) => {
      // Duplicate-name error is shown inline on the name field instead.
      if (isDuplicateFolderNameError(error)) return
      toast({ description: 'Failed to create collection.', position: 'top-right', type: 'error' })
    },
  })
}

export const useUpdateFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFolderPayload }) =>
      updateFolder(id, payload),
    onSuccess: () => {
      toast({ description: 'Collection updated.', position: 'top-right', type: 'success' })
      queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY] })
    },
    onError: (error) => {
      // Duplicate-name error is shown inline on the name field instead.
      if (isDuplicateFolderNameError(error)) return
      toast({ description: 'Failed to update collection.', position: 'top-right', type: 'error' })
    },
  })
}

export const useDeleteFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteFolder(id),
    onSuccess: () => {
      // Deleting a collection only removes membership; documents survive, so their list needs invalidating too.
      toast({ description: 'Collection deleted.', position: 'top-right', type: 'success' })
      queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] })
    },
    onError: () =>
      toast({ description: 'Failed to delete collection.', position: 'top-right', type: 'error' }),
  })
}

type MoveDocumentVariables = {
  folderId: string
  documentId: string
  documentTitle: string
  folderName: string
}

export const useAddDocumentToFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, documentId }: MoveDocumentVariables) =>
      addDocumentToFolder(folderId, documentId),
    onSuccess: (_data, { documentTitle, folderName }) => {
      toast({
        description: `${documentTitle} has been added to collection ${folderName}`,
        position: 'top-right',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] })
    },
    onError: (error) =>
      toast({
        description: movePermissionDeniedMessage(error, 'Failed to add document to collections.'),
        position: 'top-right',
        type: 'error',
      }),
  })
}

export const useRemoveDocumentFromFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, documentId }: MoveDocumentVariables) =>
      removeDocumentFromFolder(folderId, documentId),
    onSuccess: (_data, { documentTitle, folderName }) => {
      toast({
        description: `${documentTitle} has been removed from collection ${folderName}`,
        position: 'top-right',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] })
    },
    onError: (error) =>
      toast({
        description: movePermissionDeniedMessage(
          error,
          'Failed to remove document from collection.',
        ),
        position: 'top-right',
        type: 'error',
      }),
  })
}
